import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  type LastInteractedStore,
  useLastInteractedStoreMenuHref,
} from '../../../../../../context/LastInteractedStore';
import {
  useScreenHeaderAddressQuery,
  useScreenHeaderRestaurantQuery,
} from '../../../../../ScreenHeader/graphql/ScreenHeader.generated';
import { navbarV2Messages as messages } from '../../../../NavbarV2.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavbarLocationData = (params: Params) => {
  const { lastInteractedStore } = params;

  const { formatMessage } = useIntl();
  const menuHref = useLastInteractedStoreMenuHref();

  // ─────────────────────────────────────────────────────────────────────

  const addressId = lastInteractedStore?.addressId ?? '';
  const restaurantId = lastInteractedStore?.restaurantId ?? '';

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ data: addressData }] = useScreenHeaderAddressQuery({
    variables: { addressId },
    pause: !addressId,
    requestPolicy: 'cache-first',
  });

  const [{ data: restaurantData }] = useScreenHeaderRestaurantQuery({
    variables: { id: restaurantId },
    pause: !restaurantId,
    requestPolicy: 'cache-first',
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const addressName = addressData?.address?.name;
  const restaurant = restaurantData?.restaurant;

  const orderChannel = useMemo<OrderChannel>(() => {
    if (addressId) return 'delivery';
    if (restaurant?.isOutpost) return 'outpost';

    return 'pickup';
  }, [addressId, restaurant?.isOutpost]);

  const locationName = addressId ? addressName : restaurant?.name;
  const locationTitle = locationName ?? formatMessage(messages.findLocationNearYou);
  const locationTooltipChannel = orderChannel === 'outpost' ? undefined : orderChannel;
  const locationTooltipLocation = addressId ? addressData?.address : restaurant;
  const locationTooltipTarget = locationTooltipLocation ?? undefined;

  const locationEyebrow = lastInteractedStore
    ? formatMessage(messages.locationEyebrow, { orderChannel })
    : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return {
    locationEyebrow,
    locationTitle,
    locationTooltipChannel,
    locationTooltipId: addressId || restaurantId,
    locationTooltipTarget,
    menuHref,
    orderChannel,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  lastInteractedStore: LastInteractedStore;
};

type OrderChannel = 'delivery' | 'outpost' | 'pickup';
